// Purpose-built SVG visuals for Dataleo Labs projects. Each visual is a
// small interface diagram that hints at what the project actually DOES
// (a cockpit, a benchmark chart, a governance framework, a control loop)
// - replacing generic stock photos with something that says "we built
// this". Rendered inline (crisp on retina, zero network cost).
//
// The `KEY` lookup is by slug so seed data + admin-created projects can
// opt-in by just setting `slug: kiiwii` (etc.). Anything else falls back
// to the item's `image_url` or a subtle default pattern.

const CommonDefs = () => (
  <defs>
    <linearGradient id="dlGreen" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0" stopColor="#00A878" />
      <stop offset="1" stopColor="#059669" />
    </linearGradient>
    <pattern id="dlGrid" width="24" height="24" patternUnits="userSpaceOnUse">
      <path d="M24 0H0V24" fill="none" stroke="#1F2937" strokeWidth="0.5" />
    </pattern>
  </defs>
);

const Frame = ({ children }) => (
  <svg viewBox="0 0 640 360" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" className="block">
    <CommonDefs />
    <rect width="640" height="360" fill="#0f172a" />
    <rect width="640" height="360" fill="url(#dlGrid)" opacity="0.4" />
    {children}
  </svg>
);

// ---- KiiWii - bidirectional cockpit (demand ↔ supply) ----
const KiiWii = () => (
  <Frame>
    <text x="24" y="34" fontFamily="ui-monospace, monospace" fontSize="10" letterSpacing="2" fill="#00A878">KIIWII · DECISION COCKPIT</text>
    <text x="24" y="52" fontFamily="ui-serif, serif" fontSize="22" fill="#ffffff">Make demand respond to supply</text>

    {/* Supply node */}
    <g transform="translate(60,110)">
      <rect width="150" height="70" fill="#0f172a" stroke="#00A878" strokeWidth="1.5" />
      <text x="12" y="20" fontFamily="ui-monospace, monospace" fontSize="9" letterSpacing="1.5" fill="#00A878">SUPPLY</text>
      <text x="12" y="42" fontFamily="ui-monospace, monospace" fontSize="10" fill="#ffffff">Capacity   ▓▓▓▓▓░░</text>
      <text x="12" y="58" fontFamily="ui-monospace, monospace" fontSize="10" fill="#ffffff">Inventory  ▓▓▓░░░░</text>
    </g>

    {/* Demand node */}
    <g transform="translate(430,110)">
      <rect width="150" height="70" fill="#0f172a" stroke="#00A878" strokeWidth="1.5" />
      <text x="12" y="20" fontFamily="ui-monospace, monospace" fontSize="9" letterSpacing="1.5" fill="#00A878">DEMAND</text>
      <text x="12" y="42" fontFamily="ui-monospace, monospace" fontSize="10" fill="#ffffff">Forecast   ▓▓▓▓▓▓░</text>
      <text x="12" y="58" fontFamily="ui-monospace, monospace" fontSize="10" fill="#ffffff">Promo      ▓▓░░░░░</text>
    </g>

    {/* Central decision engine */}
    <g transform="translate(240,200)">
      <rect width="160" height="80" fill="#00A878" />
      <text x="80" y="30" fontFamily="ui-monospace, monospace" fontSize="9" letterSpacing="1.5" fill="#0f172a" textAnchor="middle">DECISION ENGINE</text>
      <text x="80" y="52" fontFamily="ui-serif, serif" fontSize="18" fill="#ffffff" textAnchor="middle">↺ Bidirectional</text>
      <text x="80" y="70" fontFamily="ui-monospace, monospace" fontSize="9" fill="#0f172a" textAnchor="middle">control loop</text>
    </g>

    {/* Arrows */}
    <path d="M210 145 L 240 220" stroke="#00A878" strokeWidth="1.5" fill="none" markerEnd="url(#arr)" />
    <path d="M430 145 L 400 220" stroke="#00A878" strokeWidth="1.5" fill="none" markerEnd="url(#arr)" />
    <path d="M280 200 Q 200 190 210 175" stroke="#00A878" strokeWidth="1.5" fill="none" strokeDasharray="4 3" markerEnd="url(#arr)" />
    <path d="M360 200 Q 440 190 430 175" stroke="#00A878" strokeWidth="1.5" fill="none" strokeDasharray="4 3" markerEnd="url(#arr)" />

    <defs>
      <marker id="arr" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
        <path d="M0,0 L8,4 L0,8" fill="#00A878" />
      </marker>
    </defs>
  </Frame>
);

// ---- Forecast Challenge - benchmark chart ----
const ForecastChallenge = () => (
  <Frame>
    <text x="24" y="34" fontFamily="ui-monospace, monospace" fontSize="10" letterSpacing="2" fill="#00A878">FORECAST CHALLENGE · BENCHMARK</text>
    <text x="24" y="52" fontFamily="ui-serif, serif" fontSize="22" fill="#ffffff">Can AI beat your forecast?</text>

    {/* Chart baseline */}
    <line x1="60" y1="290" x2="600" y2="290" stroke="#374151" strokeWidth="1" />
    <line x1="60" y1="120" x2="60" y2="290" stroke="#374151" strokeWidth="1" />

    {/* Actual (white dashed) */}
    <polyline points="60,240 130,225 200,235 270,210 340,215 410,195 480,205 550,185 600,190"
      fill="none" stroke="#ffffff" strokeWidth="1.5" strokeDasharray="4 4" />
    {/* Planner baseline (grey) */}
    <polyline points="60,235 130,220 200,240 270,225 340,220 410,215 480,225 550,205 600,215"
      fill="none" stroke="#9ca3af" strokeWidth="1.5" />
    {/* AI forecast (green solid) */}
    <polyline points="60,240 130,222 200,233 270,208 340,213 410,197 480,203 550,187 600,192"
      fill="none" stroke="#00A878" strokeWidth="2.5" />

    {/* Legend */}
    <g transform="translate(400,110)">
      <rect x="0" y="0" width="200" height="60" fill="#111827" stroke="#1F2937" />
      <line x1="12" y1="18" x2="30" y2="18" stroke="#00A878" strokeWidth="2.5" />
      <text x="38" y="22" fontFamily="ui-monospace, monospace" fontSize="10" fill="#ffffff">AI model - MAPE 8.2%</text>
      <line x1="12" y1="34" x2="30" y2="34" stroke="#9ca3af" strokeWidth="1.5" />
      <text x="38" y="38" fontFamily="ui-monospace, monospace" fontSize="10" fill="#9ca3af">Planner - MAPE 12.7%</text>
      <line x1="12" y1="50" x2="30" y2="50" stroke="#ffffff" strokeWidth="1.5" strokeDasharray="3 3" />
      <text x="38" y="54" fontFamily="ui-monospace, monospace" fontSize="10" fill="#ffffff">Actual demand</text>
    </g>

    <text x="60" y="330" fontFamily="ui-monospace, monospace" fontSize="9" fill="#4B5563">W1</text>
    <text x="600" y="330" fontFamily="ui-monospace, monospace" fontSize="9" fill="#4B5563" textAnchor="end">W52</text>
  </Frame>
);

// ---- Decision Governance - framework blocks ----
const DecisionGovernance = () => (
  <Frame>
    <text x="24" y="34" fontFamily="ui-monospace, monospace" fontSize="10" letterSpacing="2" fill="#00A878">DECISION GOVERNANCE · FRAMEWORK</text>
    <text x="24" y="52" fontFamily="ui-serif, serif" fontSize="22" fill="#ffffff">Govern the decision, not the tool</text>

    {/* 4 rows × 3 pillars */}
    {[
      { y: 90,  label: "RIGHTS",      cells: ["Owner", "Approvers", "Escalation"] },
      { y: 145, label: "DATA",        cells: ["Lineage", "Quality", "Signals"] },
      { y: 200, label: "MODEL",       cells: ["Assumptions", "Validation", "Auditability"] },
      { y: 255, label: "HUMAN LOOP",  cells: ["Override", "Explainability", "Trace"] },
    ].map((row) => (
      <g key={row.label} transform={`translate(0,${row.y})`}>
        <text x="24" y="30" fontFamily="ui-monospace, monospace" fontSize="9" letterSpacing="1.5" fill="#00A878">{row.label}</text>
        {row.cells.map((c, i) => (
          <g key={c} transform={`translate(${140 + i * 155},0)`}>
            <rect width="140" height="42" fill="#0f172a" stroke="#1F2937" />
            <text x="12" y="26" fontFamily="ui-monospace, monospace" fontSize="11" fill="#ffffff">{c}</text>
          </g>
        ))}
      </g>
    ))}
  </Frame>
);

// ---- Bidirectional Supply-Demand Control ----
const BSDC = () => (
  <Frame>
    <text x="24" y="34" fontFamily="ui-monospace, monospace" fontSize="10" letterSpacing="2" fill="#00A878">BSDC · CLOSED-LOOP CONTROL</text>
    <text x="24" y="52" fontFamily="ui-serif, serif" fontSize="22" fill="#ffffff">One control loop across demand & supply</text>

    {/* Ellipse loop */}
    <ellipse cx="320" cy="220" rx="240" ry="80" fill="none" stroke="#00A878" strokeWidth="1.5" strokeDasharray="6 4" />

    {[
      { x: 90,  y: 220, label: "PLAN"    },
      { x: 240, y: 140, label: "DEMAND"  },
      { x: 400, y: 140, label: "SUPPLY"  },
      { x: 550, y: 220, label: "EXECUTE" },
      { x: 400, y: 300, label: "SENSE"   },
      { x: 240, y: 300, label: "LEARN"   },
    ].map((n, i) => (
      <g key={n.label} transform={`translate(${n.x - 55},${n.y - 20})`}>
        <rect width="110" height="40" fill="#0f172a" stroke="#00A878" strokeWidth="1.5" />
        <text x="55" y="16" fontFamily="ui-monospace, monospace" fontSize="9" letterSpacing="1.5" fill="#00A878" textAnchor="middle">{`0${i + 1}`}</text>
        <text x="55" y="32" fontFamily="ui-monospace, monospace" fontSize="10" fill="#ffffff" textAnchor="middle">{n.label}</text>
      </g>
    ))}

    <text x="320" y="230" fontFamily="ui-serif, serif" fontSize="18" fill="#ffffff" textAnchor="middle">↺</text>
  </Frame>
);

const KEY = {
  kiiwii: KiiWii,
  "forecast-challenge": ForecastChallenge,
  "decision-governance": DecisionGovernance,
  "bidirectional-supply-demand-control": BSDC,
};

/**
 * Renders the labs-specific visual by slug if available, else falls back
 * to the item's `image_url`, else to an ambient decision-grid pattern.
 * Always renders inside a 16:9 frame (matches SmartImage dimensions).
 */
const LabsVisual = ({ item, className = "" }) => {
  const key = (item?.slug || "").toLowerCase();
  const Cmp = KEY[key];
  return (
    <div className={`aspect-[16/9] bg-[#0f172a] overflow-hidden ${className}`}>
      {Cmp ? <Cmp /> : item?.image_url ? (
        <img src={item.image_url} alt={item.title} width="1600" height="900" className="w-full h-full object-cover" />
      ) : <Frame />}
    </div>
  );
};

export default LabsVisual;
